import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

interface MealPlanSettingsProps {
  planId: string;
  initialName: string;
  initialStatus: string;
  onUpdate: () => void;
}

export function MealPlanSettings({ planId, initialName, initialStatus, onUpdate }: MealPlanSettingsProps) {
  const navigate = useNavigate();
  const [planName, setPlanName] = useState(initialName);
  const [status, setStatus] = useState(initialStatus);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSave = async () => {
    try {
      setSaving(true);

      const { error } = await supabase
        .from("meal_plans")
        .update({
          plan_name: planName,
          status: status,
        })
        .eq("id", planId);

      if (error) throw error;

      toast.success("Plano atualizado com sucesso!");
      onUpdate();
    } catch (error) {
      console.error("Error updating meal plan:", error);
      toast.error("Erro ao atualizar plano");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);

      // Deletar meal_items primeiro (devido à foreign key)
      const { error: itemsError } = await supabase
        .from("meal_items")
        .delete()
        .eq("meal_plan_id", planId);

      if (itemsError) throw itemsError;

      // Deletar tracking link se existir
      const { error: linkError } = await supabase
        .from("meal_tracking_links")
        .delete()
        .eq("meal_plan_id", planId);

      if (linkError && linkError.code !== 'PGRST116') throw linkError;

      // Deletar meal_plan
      const { error: planError } = await supabase
        .from("meal_plans")
        .delete()
        .eq("id", planId);

      if (planError) throw planError;

      toast.success("Plano excluído com sucesso!");
      navigate("/app/aplicativo/mealplan");
    } catch (error) {
      console.error("Error deleting meal plan:", error);
      toast.error("Erro ao excluir plano");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Informações do Plano</CardTitle>
          <CardDescription>
            Edite o nome e status do plano alimentar
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="plan-name">Nome do Plano</Label>
            <Input
              id="plan-name"
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              placeholder="Ex: Plano de Emagrecimento"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="plan-status">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger id="plan-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Ativo</SelectItem>
                <SelectItem value="paused">Pausado</SelectItem>
                <SelectItem value="completed">Finalizado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="w-full"
          >
            <Save className="mr-2 h-4 w-4" />
            {saving ? "Salvando..." : "Salvar Alterações"}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive">Zona de Perigo</CardTitle>
          <CardDescription>
            Ações irreversíveis. Tenha cuidado ao prosseguir.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="w-full" disabled={deleting}>
                <Trash2 className="mr-2 h-4 w-4" />
                {deleting ? "Excluindo..." : "Excluir Plano"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Tem certeza?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta ação não pode ser desfeita. Isso irá excluir permanentemente o plano alimentar,
                  todas as refeições e submissões associadas.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Sim, excluir plano
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  );
}
